import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { EnvMicroMasterData } from "../../config/env.micro.master-data";
import { MASTER_DATA_ENV } from "../../config/micros-env.tokens";
import {
  CurrentPeriodResponse,
  PeriodRange,
} from "../../../domain/models/master-data/current-period.model";
import { CurrentPeriodPort } from "../../../app/ports/master-data/current-period.port";

@Injectable()
export class CurrentPeriodHttpAdapter implements CurrentPeriodPort {
  private static readonly CURRENT_PERIOD_PATH = "/api/v1/periods/current";
  private static readonly PERIOD_IDS_PATH = "/api/v1/periods/ids";

  constructor(
    private readonly http: HttpClient,
    @Inject(MASTER_DATA_ENV) private readonly env: EnvMicroMasterData,
  ) {}

  getCurrent(): Observable<CurrentPeriodResponse> {
    const baseUrl = `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");

    return this.http.get<unknown>(`${baseUrl}${CurrentPeriodHttpAdapter.CURRENT_PERIOD_PATH}`).pipe(
      map((response) => {
        if (
          typeof response !== "object" ||
          response === null ||
          !Number.isFinite((response as Record<string, unknown>)["idPeriod"])
        ) {
          throw new Error("La respuesta del periodo actual es inválida.");
        }
        return response as CurrentPeriodResponse;
      }),
    );
  }

  getPeriods(year: number, month: number): Observable<readonly PeriodRange[]> {
    const baseUrl = `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");
    const params = new HttpParams()
      .set("year", String(year))
      .set("month", String(month));

    return this.http.get<unknown>(`${baseUrl}${CurrentPeriodHttpAdapter.PERIOD_IDS_PATH}`, { params }).pipe(
      map((response) => this.extractPeriods(response)),
    );
  }

  private extractPeriods(response: unknown): readonly PeriodRange[] {
    if (typeof response !== "object" || response === null) {
      throw new Error("La respuesta de ciclos es inválida.");
    }

    const periods = (response as Record<string, unknown>)["periods"];
    if (
      !Array.isArray(periods) ||
      !periods.every((period) => this.isPeriodRange(period))
    ) {
      throw new Error("La respuesta de ciclos es inválida.");
    }

    return periods;
  }

  private isPeriodRange(value: unknown): value is PeriodRange {
    if (typeof value !== "object" || value === null) return false;
    const period = value as Record<string, unknown>;
    return Number.isFinite(period["idPeriod"])
      && typeof period["initialDate"] === "string"
      && typeof period["endDate"] === "string";
  }
}
